import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/hooks/use-toast";
import { Download, FileText, Loader2, Search } from "lucide-react";
import { AVERY_TEMPLATES, getTemplate } from "./AveryTemplates";
import { BadgeCard } from "./BadgeCard";
import { exportSinglePdf, exportSinglePng } from "./exporters";
import type { Attendee, Branding } from "./types";

interface Props {
  attendees: Attendee[];
  branding: Branding | null;
  activeKey: string | null;
  setActiveKey: (k: string) => void;
  templateId: string;
  setTemplateId: (id: string) => void;
}

export function DesignerPanel({ attendees, branding, activeKey, setActiveKey, templateId, setTemplateId }: Props) {
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState<"png" | "pdf" | null>(null);
  const [search, setSearch] = useState("");

  const template = getTemplate(templateId);
  const filtered = useMemo(
    () => attendees.filter((a) => (search ? a.fullName.toLowerCase().includes(search.toLowerCase()) : true)),
    [attendees, search],
  );
  const attendee = attendees.find((a) => a.key === activeKey) ?? attendees[0] ?? null;

  const download = async (kind: "png" | "pdf") => {
    if (!attendee) return;
    setBusy(kind);
    try {
      if (kind === "png") await exportSinglePng(attendee, branding, template.badgeW, template.badgeH);
      else await exportSinglePdf(attendee, branding, template.badgeW, template.badgeH);
      toast({ title: `Downloaded ${kind.toUpperCase()}`, description: attendee.fullName });
    } catch (e: any) {
      toast({ title: "Export failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
      <Card>
        <CardHeader><CardTitle>Attendee</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Attendee name" />
            </div>
          </div>
          <div className="border rounded-md max-h-96 overflow-y-auto divide-y">
            {filtered.slice(0, 200).map((a) => (
              <button
                key={a.key}
                onClick={() => setActiveKey(a.key)}
                className={`w-full text-left p-2 text-sm hover:bg-muted/50 ${activeKey === a.key ? "bg-primary/10" : ""}`}
              >
                <div className="font-medium">{a.fullName || "Unnamed"}</div>
                <div className="text-xs text-muted-foreground">{a.region} · {a.registrationCategory}</div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground text-center">No matches.</div>
            )}
          </div>

          <div>
            <Label>Badge size / Avery template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {AVERY_TEMPLATES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Zoom</Label>
            <Slider value={[zoom * 100]} onValueChange={([v]) => setZoom(v / 100)} min={50} max={200} step={5} />
            <div className="text-xs text-muted-foreground mt-1">{Math.round(zoom * 100)}%</div>
          </div>

          <div className="flex flex-col gap-2">
            <Button onClick={() => download("png")} disabled={!attendee || busy !== null}>
              {busy === "png" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download PNG
            </Button>
            <Button variant="outline" onClick={() => download("pdf")} disabled={!attendee || busy !== null}>
              {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center bg-muted/30 rounded-lg p-6 overflow-auto min-h-[400px]">
            {attendee ? (
              <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
                <BadgeCard
                  attendee={attendee}
                  branding={branding}
                  widthIn={template.badgeW}
                  heightIn={template.badgeH}
                  dpi={150}
                />
              </div>
            ) : (
              <div className="text-muted-foreground">Select an attendee to preview a badge.</div>
            )}
          </div>
          {attendee && (
            <div className="text-xs text-muted-foreground mt-3">
              {template.badgeW}″ × {template.badgeH}″ · rendered at 300 DPI for export
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
